import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft, Search as SearchIcon, X, Send, Plus, Pencil, Trash2, EyeOff, Eye,
  Ban, CheckCircle2, Wine, Cog, PackageX, PackageCheck,
} from "lucide-react";
import { useAuth, canAccess } from "@/lib/auth";

/**
 * Fase 13 — Publish Diff Preview.
 * Read-only summary of what will change on the customer menu once the
 * admin hits Publish. Consumed via `/admin/menu/diff`. Does NOT mutate
 * any state; the "Pubblica" CTA here calls the same `/admin/menu/publish`
 * endpoint used by MenuPublishBar.
 */

const FILTERS = [
  { id: "all",    label: "Tutte" },
  { id: "added",  label: "Solo aggiunte" },
  { id: "edited", label: "Solo modifiche" },
  { id: "removed", label: "Solo eliminazioni" },
  { id: "avail",  label: "Solo disponibilità" },
  { id: "wines",  label: "Solo vini" },
];

function normalise(v) {
  return (v ?? "").toString().toLowerCase();
}

function Section({ icon: Icon, title, tone, count, testid, children }) {
  if (count === 0) return null;
  const toneCls = {
    green:  "border-emerald-200 bg-emerald-50 text-emerald-900",
    red:    "border-rose-200 bg-rose-50 text-rose-900",
    amber:  "border-amber-200 bg-amber-50 text-amber-900",
    slate:  "border-slate-200 bg-slate-50 text-slate-900",
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-900",
    wine:   "border-fuchsia-200 bg-fuchsia-50 text-fuchsia-900",
  }[tone] || "border-slate-200 bg-slate-50 text-slate-900";
  return (
    <section data-testid={testid}
             className={`rounded-2xl border ${toneCls} px-4 py-3 space-y-2`}>
      <header className="flex items-center gap-2 font-semibold">
        {Icon && <Icon className="w-4 h-4" />}
        <span>{title}</span>
        <span className="ml-auto text-xs font-normal bg-white/70 rounded-full px-2 py-0.5">
          {count}
        </span>
      </header>
      <ul className="space-y-1.5">
        {children}
      </ul>
    </section>
  );
}

function ItemRow({ name, sub, before, after, testid }) {
  return (
    <li data-testid={testid} className="text-sm bg-white/60 rounded-lg px-3 py-2">
      <div className="font-medium text-ink-900">{name}</div>
      {sub && <div className="text-xs text-ink-500 mt-0.5">{sub}</div>}
      {(before || after) && (
        <div className="text-xs text-ink-700 mt-1 flex items-center gap-2 flex-wrap">
          <span className="line-through text-ink-400">{before ?? "—"}</span>
          <span aria-hidden="true">→</span>
          <span className="font-medium">{after ?? "—"}</span>
        </div>
      )}
    </li>
  );
}

function ChangeList({ item }) {
  if (!item.changes || item.changes.length === 0) return null;
  return (
    <ul className="mt-1 space-y-0.5 text-xs text-ink-700">
      {item.changes.map((c, i) => (
        <li key={i} className="flex items-center gap-2 flex-wrap">
          <span className="text-ink-500">{c.label}:</span>
          <span className="line-through text-ink-400">{c.before ?? "—"}</span>
          <span aria-hidden="true">→</span>
          <span className="font-medium">{c.after ?? "—"}</span>
        </li>
      ))}
    </ul>
  );
}

export default function PublishDiffPreview() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canPublish = canAccess(user, "admin");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [publishOpen, setPublishOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/menu/diff");
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/menu/publish", { notes: notes.trim() || null });
      toast.success(`Menu pubblicato · v.${r.data.version}`);
      setPublishOpen(false); setNotes("");
      nav("/admin/menu");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // -------- Filtering --------
  const q = normalise(query).trim();
  const passQ = (item) => {
    if (!q) return true;
    const parts = [
      item.name, item.category_name, item.department,
      ...(item.changes || []).flatMap((c) => [c.label, c.before, c.after]),
    ].filter(Boolean).map(normalise);
    return parts.some((p) => p.includes(q));
  };

  const groups = data?.groups || {};
  const flt = (arr) => (arr || []).filter(passQ);

  const showAdded   = filter === "all" || filter === "added";
  const showEdited  = filter === "all" || filter === "edited";
  const showRemoved = filter === "all" || filter === "removed";
  const showAvail   = filter === "all" || filter === "avail";
  const showWines   = filter === "all" || filter === "wines";

  const vis = useMemo(() => {
    if (!data) return null;
    const wines = groups.wines || {};
    return {
      new_products:            showAdded            ? flt(groups.new_products) : [],
      removed_products:        showRemoved          ? flt(groups.removed_products) : [],
      modified_products:       showEdited           ? flt(groups.modified_products) : [],
      became_unavailable:      showAvail            ? flt(groups.became_unavailable) : [],
      became_available:        showAvail            ? flt(groups.became_available) : [],
      hidden_categories:       (showEdited || showRemoved) ? flt(groups.hidden_categories) : [],
      reactivated_categories:  (showEdited || showAdded)   ? flt(groups.reactivated_categories) : [],
      new_categories:          showAdded            ? flt(groups.new_categories) : [],
      removed_categories:      showRemoved          ? flt(groups.removed_categories) : [],
      modified_categories:     showEdited           ? flt(groups.modified_categories) : [],
      disabled_departments:    (showEdited || showRemoved)
                                 ? (groups.disabled_departments || []).filter((d) => !q || normalise(d).includes(q))
                                 : [],
      reenabled_departments:   (showEdited || showAdded)
                                 ? (groups.reenabled_departments || []).filter((d) => !q || normalise(d).includes(q))
                                 : [],
      wines_new:               showWines            ? flt(wines.new) : [],
      wines_modified:          showWines            ? flt(wines.modified) : [],
      wines_removed:           showWines            ? flt(wines.removed) : [],
      wines_became_unavailable: showWines           ? flt(wines.became_unavailable) : [],
      wines_became_available:  showWines            ? flt(wines.became_available) : [],
    };
  }, [data, groups, filter, q]);

  const totalVisible = useMemo(() => {
    if (!vis) return 0;
    return Object.values(vis).reduce((s, arr) => s + (arr?.length || 0), 0);
  }, [vis]);

  if (loading) {
    return <div className="p-8 text-ink-500" data-testid="diff-loading">Caricamento anteprima…</div>;
  }
  if (!data) return null;

  const pending = data.has_pending_changes;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4" data-testid="publish-diff-page">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => nav("/admin/menu")}
                data-testid="diff-close-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Chiudi
        </Button>
        <div className="ml-1">
          <h1 className="text-xl font-semibold">Anteprima modifiche</h1>
          <p className="text-xs text-ink-500">
            Confronto con la versione pubblicata v.{data.version}
            {" · "}Totale modifiche: <b data-testid="diff-total-count">{data.counts.total}</b>
          </p>
        </div>
        <div className="ml-auto">
          {canPublish && pending && (
            <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                        data-testid="diff-publish-btn">
                  <Send className="w-4 h-4 mr-1" /> Pubblica
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle>Pubblica {data.counts.total} modific{data.counts.total === 1 ? "a" : "he"}?</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <p className="text-sm text-ink-500">
                    Il menu cliente verrà aggiornato immediatamente. La versione attuale (v.{data.version}) resterà archiviata.
                  </p>
                  <div>
                    <label className="text-xs font-medium">Nota (opzionale)</label>
                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                              placeholder="es. Menu invernale, prezzi ritoccati…"
                              data-testid="diff-publish-notes" rows={3} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setPublishOpen(false)}>Annulla</Button>
                  <Button onClick={publish} disabled={busy}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="diff-publish-confirm">
                    {busy ? "Pubblico…" : "Pubblica ora"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Empty state */}
      {!pending && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-900 p-6 text-center"
             data-testid="diff-empty">
          <CheckCircle2 className="w-6 h-6 mx-auto mb-2" />
          <p className="font-medium">Nessuna modifica in sospeso.</p>
          <p className="text-xs text-emerald-800 mt-1">
            Il menu draft coincide con la versione pubblicata v.{data.version}.
          </p>
        </div>
      )}

      {pending && (
        <>
          {/* Search + filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)}
                     placeholder="Cerca prodotto, categoria, reparto…"
                     className="pl-9 pr-8"
                     data-testid="diff-search-input" />
              {query && (
                <button onClick={() => setQuery("")} data-testid="diff-search-clear"
                        aria-label="Pulisci ricerca"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              {FILTERS.map((f) => (
                <button key={f.id} onClick={() => setFilter(f.id)}
                        data-testid={`diff-filter-${f.id}`}
                        className={`text-xs rounded-full border px-3 py-1 transition
                                    ${filter === f.id
                                      ? "bg-ink-900 text-white border-ink-900"
                                      : "bg-white border-ink-200 hover:bg-ink-50"}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Zero results notice */}
          {totalVisible === 0 && (
            <div className="rounded-xl border border-dashed border-ink-200 p-6 text-center text-sm text-ink-500"
                 data-testid="diff-no-match">
              Nessuna modifica corrisponde ai filtri correnti.
            </div>
          )}

          {/* Groups */}
          <div className="grid gap-3">
            <Section icon={Plus} title="Nuovi prodotti" tone="green"
                     count={vis.new_products.length} testid="grp-new-products">
              {vis.new_products.map((p) => (
                <ItemRow key={p.id} name={p.name} testid={`diff-new-prod-${p.id}`}
                         sub={[p.category_name, p.price, p.department].filter(Boolean).join(" · ")} />
              ))}
            </Section>

            <Section icon={Pencil} title="Prodotti modificati" tone="amber"
                     count={vis.modified_products.length} testid="grp-modified-products">
              {vis.modified_products.map((p) => (
                <li key={p.id} data-testid={`diff-mod-prod-${p.id}`}
                    className="text-sm bg-white/60 rounded-lg px-3 py-2">
                  <div className="font-medium text-ink-900">{p.name}</div>
                  {p.category_name && <div className="text-xs text-ink-500">{p.category_name}</div>}
                  <ChangeList item={p} />
                </li>
              ))}
            </Section>

            <Section icon={Ban} title="Prodotti diventati esauriti" tone="red"
                     count={vis.became_unavailable.length} testid="grp-unavailable">
              {vis.became_unavailable.map((p) => (
                <ItemRow key={p.id} name={p.name}
                         sub={p.category_name}
                         testid={`diff-unavail-${p.id}`} />
              ))}
            </Section>

            <Section icon={PackageCheck} title="Prodotti tornati disponibili" tone="green"
                     count={vis.became_available.length} testid="grp-reavailable">
              {vis.became_available.map((p) => (
                <ItemRow key={p.id} name={p.name}
                         sub={p.category_name}
                         testid={`diff-reavail-${p.id}`} />
              ))}
            </Section>

            <Section icon={Trash2} title="Prodotti rimossi" tone="red"
                     count={vis.removed_products.length} testid="grp-removed-products">
              {vis.removed_products.map((p) => (
                <ItemRow key={p.id} name={p.name} sub={p.category_name}
                         testid={`diff-removed-prod-${p.id}`} />
              ))}
            </Section>

            <Section icon={Plus} title="Nuove categorie" tone="green"
                     count={vis.new_categories.length} testid="grp-new-cats">
              {vis.new_categories.map((c) => (
                <ItemRow key={c.id} name={c.name} sub={c.department}
                         testid={`diff-new-cat-${c.id}`} />
              ))}
            </Section>

            <Section icon={EyeOff} title="Categorie nascoste" tone="slate"
                     count={vis.hidden_categories.length} testid="grp-hidden-cats">
              {vis.hidden_categories.map((c) => (
                <ItemRow key={c.id} name={c.name} testid={`diff-hidden-cat-${c.id}`} />
              ))}
            </Section>

            <Section icon={Eye} title="Categorie riattivate" tone="green"
                     count={vis.reactivated_categories.length} testid="grp-reactivated-cats">
              {vis.reactivated_categories.map((c) => (
                <ItemRow key={c.id} name={c.name} testid={`diff-react-cat-${c.id}`} />
              ))}
            </Section>

            <Section icon={Pencil} title="Categorie modificate" tone="amber"
                     count={vis.modified_categories.length} testid="grp-modified-cats">
              {vis.modified_categories.map((c) => (
                <li key={c.id} data-testid={`diff-mod-cat-${c.id}`}
                    className="text-sm bg-white/60 rounded-lg px-3 py-2">
                  <div className="font-medium text-ink-900">{c.name}</div>
                  <ChangeList item={c} />
                </li>
              ))}
            </Section>

            <Section icon={Trash2} title="Categorie eliminate" tone="red"
                     count={vis.removed_categories.length} testid="grp-removed-cats">
              {vis.removed_categories.map((c) => (
                <ItemRow key={c.id} name={c.name} testid={`diff-rem-cat-${c.id}`} />
              ))}
            </Section>

            <Section icon={Cog} title="Reparti spenti" tone="slate"
                     count={vis.disabled_departments.length} testid="grp-disabled-depts">
              {vis.disabled_departments.map((d) => (
                <ItemRow key={d} name={d} testid={`diff-dept-off-${d}`} />
              ))}
            </Section>

            <Section icon={Cog} title="Reparti riattivati" tone="green"
                     count={vis.reenabled_departments.length} testid="grp-reenabled-depts">
              {vis.reenabled_departments.map((d) => (
                <ItemRow key={d} name={d} testid={`diff-dept-on-${d}`} />
              ))}
            </Section>

            {/* Wines */}
            {(vis.wines_new.length + vis.wines_modified.length + vis.wines_removed.length +
              vis.wines_became_unavailable.length + vis.wines_became_available.length) > 0 && (
              <section className="rounded-2xl border border-fuchsia-200 bg-fuchsia-50/50 p-4 space-y-3"
                       data-testid="grp-wines">
                <header className="flex items-center gap-2 font-semibold text-fuchsia-900">
                  <Wine className="w-4 h-4" /> Carta dei Vini
                </header>

                <Section icon={Plus} title="Nuovi vini" tone="green"
                         count={vis.wines_new.length} testid="grp-wines-new">
                  {vis.wines_new.map((p) => (
                    <ItemRow key={p.id} name={p.name}
                             sub={[p.category_name, p.price].filter(Boolean).join(" · ")}
                             testid={`diff-wine-new-${p.id}`} />
                  ))}
                </Section>

                <Section icon={Pencil} title="Vini modificati" tone="amber"
                         count={vis.wines_modified.length} testid="grp-wines-mod">
                  {vis.wines_modified.map((p) => (
                    <li key={p.id} data-testid={`diff-wine-mod-${p.id}`}
                        className="text-sm bg-white/60 rounded-lg px-3 py-2">
                      <div className="font-medium text-ink-900">{p.name}</div>
                      {p.category_name && <div className="text-xs text-ink-500">{p.category_name}</div>}
                      <ChangeList item={p} />
                    </li>
                  ))}
                </Section>

                <Section icon={Ban} title="Vini esauriti" tone="red"
                         count={vis.wines_became_unavailable.length} testid="grp-wines-unavail">
                  {vis.wines_became_unavailable.map((p) => (
                    <ItemRow key={p.id} name={p.name} sub={p.category_name}
                             testid={`diff-wine-unavail-${p.id}`} />
                  ))}
                </Section>

                <Section icon={PackageCheck} title="Vini tornati disponibili" tone="green"
                         count={vis.wines_became_available.length} testid="grp-wines-reavail">
                  {vis.wines_became_available.map((p) => (
                    <ItemRow key={p.id} name={p.name} sub={p.category_name}
                             testid={`diff-wine-reavail-${p.id}`} />
                  ))}
                </Section>

                <Section icon={Trash2} title="Vini rimossi" tone="red"
                         count={vis.wines_removed.length} testid="grp-wines-removed">
                  {vis.wines_removed.map((p) => (
                    <ItemRow key={p.id} name={p.name} sub={p.category_name}
                             testid={`diff-wine-rem-${p.id}`} />
                  ))}
                </Section>
              </section>
            )}
          </div>

          {/* Footer CTA */}
          <div className="sticky bottom-0 bg-white/90 backdrop-blur border-t border-ink-100 pt-3 pb-2 flex items-center gap-2">
            <Button variant="outline" onClick={() => nav("/admin/menu")}
                    data-testid="diff-close-footer-btn">
              Chiudi
            </Button>
            {canPublish && pending && (
              <Button className="ml-auto rounded-full bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => setPublishOpen(true)}
                      data-testid="diff-publish-footer-btn">
                <Send className="w-4 h-4 mr-1" /> Pubblica
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
